import { useMemo, useState } from "react"
import { useParams } from "react-router-dom"
import { PaginationState } from "@tanstack/react-table"
import { ALLOWED_ROLES_BY_ROUTE } from "../../common/constants"
import BrandActivities from "../../common/ui/brand-details/activities"
import BrandDetails from "../../common/ui/brand-details/details"
import BrandDetailsHeader from "../../common/ui/brand-details/header"
import BrandDetailsIntegrations from "../../common/ui/integrations/integrationTable"
import BrandDetailsLeads from "../../common/ui/brand-details/leads"
import BrandDetailsLoanOfficers from "../../common/ui/brand-details/loan-officers/BrandDetailsLoanOfficers"
import PricingLogs from "../../common/ui/pricing-logs"
import ManageBrand from "../../common/ui/brands/manage"
import ManageIntegration from "../../common/ui/integrations/manage"
import BBDialog from "../../common/ui/dialog"
import Tabs from "../../common/ui/tabs"
import { RequireRole } from "../../common/validation"
import {
  useFetchIntegrationsQuery,
  useFetchLeadsByBrandQuery,
  useFetchBrandQuery,
  useFetchOrgDetailsQuery,
  useGetIntegrationDetailsQuery,
  useGetStatusQuery
} from "../../redux/queries"
import { Integration } from "../../types"
import { If } from "../../common/ui"

const BrandDetailsPage = () => {
  const { brandId: id, orgId } = useParams()

  const { data: orgDetails } = useFetchOrgDetailsQuery(
    { id: orgId ?? "" },
    { skip: !orgId }
  )
  const { data: brand, isFetching } = useFetchBrandQuery(id ?? "", {
    skip: !id
  })

  const { data: leads, isLoading: isLoadingLeads } = useFetchLeadsByBrandQuery(
    id ?? "",
    { skip: !id }
  )

  const [integrationsPagination, setIntegrationsPagination] =
    useState<PaginationState>({
      pageIndex: 0,
      pageSize: 10
    })

  const [selectedIntegrationId, setSelectedIntegrationId] = useState<
    string | null
  >(null)

  const { data: integrationsResponse, isLoading: isLoadingIntegrationsAll } =
    useFetchIntegrationsQuery(
      {
        org_id: brand?.org_id,
        brand_id: id as string,
        limit: integrationsPagination.pageSize,
        offset:
          integrationsPagination.pageIndex * integrationsPagination.pageSize
      },
      {
        skip: !brand?.org_id,
        refetchOnMountOrArgChange: true
      }
    )

  const integrations = useMemo(
    () =>
      (integrationsResponse?.results as Integration[])?.filter(
        ({ brand_id }) => brand_id === id
      ) || [],
    [id, integrationsResponse?.results]
  )

  const { data: integration, isLoading: isLoadingIntegration } =
    useGetIntegrationDetailsQuery(selectedIntegrationId ?? "", {
      skip: !selectedIntegrationId
    })

  const { data: statusData, isLoading: isLoadingStatus } = useGetStatusQuery(
    { integrationID: selectedIntegrationId ?? "" },
    { skip: !selectedIntegrationId }
  )

  const [manageBrandVisible, setManageBrandVisible] = useState(false)
  const [deleteBrandVisible, setDeleteBrandVisible] = useState(false)

  const closeManageBrandSidebar = () => setManageBrandVisible(false)
  const openDeleteBrand = () => setDeleteBrandVisible(true)
  const closeDeleteBrand = () => setDeleteBrandVisible(false)

  const [tab, setTab] = useState("brand-details")

  const onChangeTab = (id: string) => setTab(id)

  const handleEdit = () => {
    setManageBrandVisible(true)
  }

  const handleDelete = () => {}

  const handleSelectIntegration = (integrationId: string) => {
    setSelectedIntegrationId(integrationId)
  }

  const handleCloseIntegration = () => {
    setSelectedIntegrationId(null)
  }

  const tabContent = {
    "brand-details": brand && <BrandDetails brand={brand} />,
    activity: brand && <BrandActivities brand={brand} />,
    "loan-officers": <BrandDetailsLoanOfficers brandId={id} />,
    leads: <BrandDetailsLeads data={leads ?? []} isLoading={isLoadingLeads} />,
    integrations: (
      <BrandDetailsIntegrations
        data={integrations ?? []}
        isLoading={isLoadingIntegrationsAll}
        onSelect={handleSelectIntegration}
        totalCount={integrationsResponse?.total}
        pagination={integrationsPagination}
        setPagination={setIntegrationsPagination}
      />
    ),
    "pricing-log": <PricingLogs id={{ bank_id: id }} />
  }

  return (
    <RequireRole allowedRoles={ALLOWED_ROLES_BY_ROUTE.brands}>
      <div className="h-full bg-white">
        <If
          isLoading={isFetching}
          condition={!!brand}
          then={
            <>
              <BrandDetailsHeader
                brand={brand!}
                onClickDelete={openDeleteBrand}
                onClickEdit={handleEdit}
                breadcrumbs={
                  orgDetails
                    ? [
                        {
                          name: "Organizations",
                          href: "/organizations"
                        },
                        {
                          name: orgDetails.name,
                          href: `/organizations/${orgDetails.id}`
                        },
                        {
                          name: brand?.name as string,
                          href: "#",
                          current: true
                        }
                      ]
                    : undefined
                }
              />
              <div>
                <Tabs
                  className="px-6"
                  currentTab={tab}
                  tabs={[
                    { id: "brand-details", label: "Brand Details" },
                    { id: "activity", label: "Activity" },
                    { id: "loan-officers", label: "Loan Officers" },
                    { id: "leads", label: "Leads" },
                    { id: "integrations", label: "Brand Integrations" },
                    { id: "pricing-log", label: "Pricing Log" }
                  ]}
                  onChange={onChangeTab}
                />
                <div className="bg-white">
                  {tabContent[tab as keyof typeof tabContent]}
                </div>
              </div>
              <ManageBrand
                open={manageBrandVisible}
                data={brand}
                isInSidePanel
                onClose={closeManageBrandSidebar}
              />
              <BBDialog
                open={deleteBrandVisible}
                onClose={closeDeleteBrand}
                title="Delete brand"
                body="By doing this all the information stored in the system about this brand will be deleted. Are you sure?"
                okLabel="Yes, Delete"
                cancelLabel="Cancel"
                onOk={handleDelete}
                onCancel={closeDeleteBrand}
              />
              <ManageIntegration
                open={!!selectedIntegrationId}
                isCreate={false}
                integrationId={selectedIntegrationId ?? undefined}
                integration={integration}
                statusData={statusData}
                isLoadingStatus={isLoadingStatus}
                isLoadingIntegration={isLoadingIntegration}
                onClose={handleCloseIntegration}
              />
            </>
          }
          else={<div className="p-6">Brand not found</div>}
        />
      </div>
    </RequireRole>
  )
}

export default BrandDetailsPage
